import { envConfig } from '#constants/envConfig';

/**
 * Native push registration for the Capacitor shell (@capacitor/push-notifications).
 * Requests OS permission, registers with FCM and resolves the native registration
 * token. Gated on IS_NATIVE so the web build tree-shakes the plugin away.
 *
 * The token is sent to the gateway with platform 'ANDROID' (see usePushNotifications),
 * and PushSenderService then delivers a notification message the OS renders even when
 * the app is closed.
 */
export async function registerNativePush(): Promise<string | null> {
  if (!envConfig.IS_NATIVE) {
    return null;
  }

  const { PushNotifications } = await import('@capacitor/push-notifications');

  let permission = await PushNotifications.checkPermissions();
  if (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale') {
    permission = await PushNotifications.requestPermissions();
  }
  if (permission.receive !== 'granted') {
    return null;
  }

  return new Promise<string | null>((resolve) => {
    let settled = false;
    const finish = (value: string | null) => {
      if (!settled) {
        settled = true;
        resolve(value);
      }
    };

    void PushNotifications.addListener('registration', (token) => finish(token.value));
    void PushNotifications.addListener('registrationError', () => finish(null));
    void PushNotifications.register();

    // Don't hang the enable flow if FCM never calls back.
    setTimeout(() => finish(null), 15_000);
  });
}

/**
 * Current OS notification permission state, or null on web / when the plugin is unavailable.
 * 'denied' on Android 13+ means the request dialog will no longer appear (permanently denied)
 * → the onboarding must send the user to system settings instead of re-requesting.
 */
export async function getPushPermissionState(): Promise<
  'prompt' | 'prompt-with-rationale' | 'granted' | 'denied' | null
> {
  if (!envConfig.IS_NATIVE) {
    return null;
  }
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const permission = await PushNotifications.checkPermissions();
    return permission.receive;
  } catch {
    return null;
  }
}
